import { Box } from "@mui/material";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import InfoSubTitle from "../../../common/InfoSubTitle";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { errorLogger } from "@/Common/helpers/logger";
import { useState } from "react";
import { IPVRMaxGraphValues, ISystemSettings } from "@/Settings/types/Common/model";
import { TPropertyParam } from "@/Settings/types/SystemSettings/pVRMaxValues";

const selectableOptions = Array.from({ length: 8 }, (_, i) => i + 1).map((key) => ({ value: key, label: `$${key * 1000}` }));

type TMaxPVRProps = {
	title: string;
	info: string;
	locationId: string;
	property: TPropertyParam;
	selected: { value: number; label: string };
	pvrMaxGraphValues?: IPVRMaxGraphValues;
	disabled: boolean;
};

const MaxPVR = (props: TMaxPVRProps) => {
	const { title, info, disabled, property, locationId, selected, pvrMaxGraphValues = {} } = props;
	const [fieldError, setFieldError] = useState(false);
	const queryClient = useQueryClient();
	const { mutate, isPending, isError } = useMutation({
		mutationKey: ["systemSettings", locationId],
		mutationFn: (input: IPVRMaxGraphValues) => updateSystemSettings(locationId, { pvrMaxGraphValues: input }, "MAX_PVR_VALUES"),
		onError: (error) => errorLogger(error.message),
		onSuccess: (data: ISystemSettings) =>
			queryClient.setQueryData(["systemSettings", locationId], (oldData: ISystemSettings) => ({
				...oldData,
				pvrMaxGraphValues: { ...oldData.pvrMaxGraphValues, [property]: data.pvrMaxGraphValues[property] },
			})),
	});

	const handleMutation = (mutationSelected?: { value: number; label: string }) => {
		const value = mutationSelected?.value;
		if (value) {
			const update = {
				...(pvrMaxGraphValues as IPVRMaxGraphValues),
				[property]: value,
			};
			setFieldError(false);
			mutate(update);
		} else {
			setFieldError(true);
		}
	};

	return (
		<Box>
			<InfoSubTitle title={title} info={info} showInfo className="!mb-[12px]" />
			<SmartSelector
				showBy={"label"}
				selectBy={"value"}
				options={selectableOptions}
				selected={selected}
				disabled={isPending || disabled}
				inputProps={{
					placeholder: "Select an amount",
				}}
				listBoxProps={{
					sx: { maxHeight: "220px" },
				}}
				sx={{ width: "150px" }}
				error={isError || fieldError}
				onChangeAction={(value) => handleMutation(value as { value: number; label: string })}
			/>
		</Box>
	);
};

export default MaxPVR;
